import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio/index";
import { debounce } from "lodash";

import { MotiRichTextEditor } from "@components/MotiRichTextEditor";
import { NotebookService } from "@services/notebook/notebookService.ts";
import { Note } from "@models/note.model.ts";
import { notebookStore } from "../../notebookStore.ts";

const notebookService = new NotebookService();

interface NoteEditorProps {
    notebookId: number;
}

export const NoteEditor = (props: NoteEditorProps) => {
    const { notebookId } = props;

    const notebookStoreSnap = useSnapshot(notebookStore);
    const [editorValue, setEditorValue] = useState<string>("");

    const isCreatingNote = useRef(false);
    const skipNextHydrate = useRef(false);
    const needsRefresh = useRef(false);

    useEffect(() => {
        const channel = new BroadcastChannel("notebook-sync");
        const onMessage = (e: MessageEvent) => {
            if (
                e.data?.type === "note-updated" &&
                e.data.noteId === notebookStore.selectedNote.id
            ) {
                needsRefresh.current = true;
            }
        };
        const onFocus = () => {
            if (needsRefresh.current) {
                needsRefresh.current = false;
                notebookStore.noteRefreshKey++;
            }
        };
        channel.addEventListener("message", onMessage);
        window.addEventListener("focus", onFocus);
        return () => {
            channel.removeEventListener("message", onMessage);
            window.removeEventListener("focus", onFocus);
            channel.close();
        };
    }, []);

    useEffect(() => {
        if (notebookStore.selectedNote.id === -1) {
            setEditorValue("");
            return;
        }

        if (skipNextHydrate.current) {
            skipNextHydrate.current = false;
            return;
        }

        const fetchAndSet = async () => {
            try {
                const note = await notebookService.getNoteDetails(
                    notebookId,
                    notebookStore.selectedNote.id,
                );
                setEditorValue(joinTitleAndBody(note.title, note.content));
            } catch (error) {
                console.error("Get Notes:", error);
                throw error;
            }
        };

        fetchAndSet().then();
    }, [notebookStoreSnap.selectedNote.id, notebookStoreSnap.noteRefreshKey, notebookId]);

    const handleChange = (html: string) => {
        setEditorValue(html);

        if (notebookStore.selectedNote.id === -1) {
            if (isEmptyContent(html)) return;
            if (isCreatingNote.current) return;
            isCreatingNote.current = true;
            const { title, body } = splitTitleAndBody(html);
            notebookService
                .createNote(notebookId, null, title, body)
                .then((newNote) => {
                    skipNextHydrate.current = true;
                    notebookStore.selectedNote = newNote;
                    notebookStore.pendingNote = newNote;
                })
                .catch((err) => console.error("Create note failed:", err))
                .finally(() => {
                    isCreatingNote.current = false;
                });
            return;
        }

        const { title, body } = splitTitleAndBody(html);
        notebookStore.selectedNote.title = title;
        notebookStore.selectedNote.content = body;

        saveNote(notebookId, notebookStore.selectedNote);
    };

    const note = notebookStoreSnap.selectedNote;
    const campaigns = notebookStoreSnap.campaigns ?? [];

    const changeCampaign = (value: string) => {
        // "" is Engagement-wide, which is a real choice and not the absence of one.
        notebookStore.selectedNote.launch_id = value === "" ? null : Number(value);
        saveNote(notebookId, notebookStore.selectedNote);
    };

    return (
        <div style={{ height: "100%" }}>
            {/* Which campaign this note is about, and when it was written (MPN-1640).
                The date is shown, never typed -- it is the note's own create_date. */}
            {note.id !== -1 && (
                <div className="note-editor__meta">
                    <select
                        className="note-editor__campaign"
                        aria-label="Campaign this note is about"
                        value={note.launch_id == null ? "" : String(note.launch_id)}
                        onChange={(e) => changeCampaign(e.target.value)}
                    >
                        <option value="">Engagement-wide</option>
                        {campaigns.map((c) => (
                            <option key={c.id} value={String(c.id)}>{c.name ?? `Campaign ${c.id}`}</option>
                        ))}
                    </select>
                    {note.create_date && (
                        <span className="note-editor__date">
                            Auto-dated: {new Date(note.create_date).toLocaleDateString(undefined, {
                                month: "short", day: "numeric", year: "numeric",
                            })}
                        </span>
                    )}
                </div>
            )}
            <MotiRichTextEditor
                value={editorValue}
                onChange={handleChange}
                placeholder="Untitled note — start typing"
                emptyBlockType="h1"
            />
        </div>
    );
};

const saveChannel = new BroadcastChannel("notebook-sync");

let pendingSaveNoteId: number | null = null;

const debouncedSave = debounce(
    async (notebookId: number, note: Note) => {
        pendingSaveNoteId = null;
        try {
            await notebookService.updateNote(notebookId, note);
            saveChannel.postMessage({ type: "note-updated", noteId: note.id });
        } catch (error) {
            console.error("Save failed:", error);
        }
    },
    500,
    { maxWait: 1000 },
);

export function saveNote(notebookId: number, note: Note) {
    pendingSaveNoteId = note.id;
    debouncedSave(notebookId, note);
}

export function cancelPendingSaveFor(noteId: number) {
    if (pendingSaveNoteId === noteId) {
        pendingSaveNoteId = null;
        debouncedSave.cancel();
    }
}

function isEmptyContent(html: string): boolean {
    if (!html) return true;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return (doc.body.textContent ?? "").trim() === "";
}

function splitTitleAndBody(html: string): { title: string; body: string } {
    if (!html) return { title: "", body: "" };
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const first = doc.body.firstElementChild;
    let title = "";
    if (first) {
        title = first.textContent ?? "";
        first.remove();
    }
    return { title, body: doc.body.innerHTML.trim() };
}

function joinTitleAndBody(title: string, body: string): string {
    return `<h1>${escapeHtml(title)}</h1>${body || ""}`;
}

function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (ch) => {
        switch (ch) {
            case "&": return "&amp;";
            case "<": return "&lt;";
            case ">": return "&gt;";
            case '"': return "&quot;";
            case "'": return "&#39;";
            default: return ch;
        }
    });
}
